import React from 'react';
import { Avatar, Snippet, Link } from '@heroui/react';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import {
  LinkIcon,
  EyeIc,
  InfoCircle,
  OnlyFansIc,
  InstagramIc,
  GoogleIc,
  TwitterIc,
  IcReddit,
  IcTg,
  IcTikTok,
  IcYoutube,
  IcAnotherSoc,
} from '../../../../components/icons';
import { CampaignCellProps } from '../types';
import { getCampaignNameFromLink } from '../../../../utils/getCampaignName';

const sourceIcons: Record<string, React.ReactNode> = {
  only_fans: <OnlyFansIc size={14} fill="#11181C" />,
  reddit: <IcReddit size={14} />,
  instagram: <InstagramIc size={14} fill="#11181C" />,
  tiktok: <IcTikTok size={14} />,
  google: <GoogleIc size={14} fill="#11181C" />,
  telegram: <IcTg size={14} />,
  twitter: <TwitterIc size={14} fill="#11181C" />,
  youtube: <IcYoutube size={14} />,
  another: <IcAnotherSoc size={14} />,
};

const CampaignCell: React.FC<CampaignCellProps> = ({ campaign }) => {
  const { t } = useTranslations();

  const sourceIcon = sourceIcons[campaign.source] || sourceIcons.another;
  const trackLink = `https://onlyfans.com/lunasidischach/${campaign.model.track_link_id || campaign._id?.slice(-4)}`;

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const getSourceLabel = (source: string) => {
    if (!source) return '';
    const sourceLabels: Record<string, string> = {
      only_fans: 'OnlyFans',
      reddit: 'Reddit',
      instagram: 'Instagram',
      tiktok: 'TikTok',
      google: 'Google',
      telegram: 'Telegram',
      twitter: 'Twitter',
      youtube: 'YouTube',
      another: t('campaigns.filters.sources.other') || 'Other',
    };
    return sourceLabels[source] || source;
  };

  // Use new API response structure - check root level transitions_count first, then model
  const clicks =
    campaign.transitions_count ??
    campaign.model?.transitions_count ??
    campaign.statistics?.clicks ??
    0;
  // Calculate subscription rate: % of users who subscribed after clicking
  const subscriptionPercent =
    clicks > 0 ? Math.round((campaign.subscribed_count / clicks) * 100) : 0;
  // Commission is now under offer
  const commission = campaign.offer?.commission || 0;

  return (
    <div className="flex flex-col gap-2">
      {/* Partner Info */}
      <div className="flex items-start gap-4">
        <Avatar
          src={campaign.model.avatar}
          showFallback
          name={campaign.model.name?.slice(0, 1)}
          size="sm"
          className="w-8 h-8 flex-shrink-0"
          classNames={{
            base: 'rounded-2',
          }}
        />
        <div className="flex flex-col justify-center gap-1">
          {campaign.owner && (
            <div className="flex items-center gap-2">
              <Avatar
                src={campaign.owner.avatar}
                name={campaign.owner.name}
                showFallback
                size="sm"
                className="w-5 h-5 rounded-full"
              />
              <ResponsiveTooltip content={t('campaigns.cell.marketerNicknameTooltip')} placement="top">
                <Typography variant="body2">
                  {campaign.owner.name}
                </Typography>
              </ResponsiveTooltip>
            </div>
          )}
          <div className="flex items-center gap-2">
            <ResponsiveTooltip content={t('campaigns.cell.campaignIdTooltip')} placement="top">
              <Typography variant="body3" className="text-auth-ink font-medium text-sm leading-5">
                {getCampaignNameFromLink(campaign.model.link, t)}
              </Typography>
            </ResponsiveTooltip>
            <Link
              href={`https://onlyfans.com/${campaign.model.of_user_name}`}
              className="text-sm text-partner flex items-center gap-1"
              isExternal
            >
              @{campaign.model.of_user_name}
            </Link>
          </div>

          <div className="flex items-center gap-2 flex-wrap mt-1">
            {/* Date Chip */}
            <ResponsiveTooltip content={t('campaigns.cell.sourceDateTooltip')} placement="top">
              <div className="bg-[rgba(120,40,200,0.2)] rounded-full px-2 py-0 h-6 flex items-center justify-center">
                <span className="text-accent text-xs font-normal leading-4">
                  {getSourceLabel(campaign.source)} {formatDate(campaign.created_at)}
                </span>
              </div>
            </ResponsiveTooltip>

            {/* Percent Info */}
            <div className="flex items-center gap-0.5">
              <span className="text-auth-ink text-sm font-normal leading-[1.125rem]">
                {commission}%
              </span>
              <ResponsiveTooltip content={t('campaigns.cell.commissionTooltip')} placement="top">
                <div className="cursor-pointer">
                  <InfoCircle size={16} className="text-neutral-8" />
                </div>
              </ResponsiveTooltip>
            </div>

            {/* Platform Chip */}
            <div className="border border-auth-strength-none rounded-3.5 px-2 py-0 h-6 flex items-center gap-1">
              <div className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center [&_svg]:h-auto [&_svg]:w-full [&_svg]:max-h-full">
                {sourceIcon}
              </div>
              <span className="text-auth-ink text-xs font-normal leading-4">
                {getSourceLabel(campaign.source)}
              </span>
            </div>

            {/* Type Text */}
            <div className="flex items-center">
              <span className="text-auth-ink text-xs font-semibold leading-4">
                {/* @ts-ignore */}
                {(campaign.type === 'free_trial' || campaign.type === 'free trial') ? 'Free-Trial' : (t('campaigns.type.byLink') || 'По ссылке')}
              </span>
            </div>
          </div>

          {/* Link Pill */}

          <div className="flex items-center gap-1 ">
            <Snippet
              symbol={null}
              codeString={campaign.model.link}
              className="bg-husky rounded-full px-2 py-0.5"
              classNames={{
                base: 'bg-husky rounded-full px-2 py-[0.1875rem] flex items-center gap-1 shadow-none',
                pre: 'bg-transparent p-0 flex items-center gap-1 max-w-[13.75rem]',
                copyButton: 'text-partner min-w-5 h-5',
                checkIcon: 'text-partner',
                content: 'flex items-center gap-1 max-w-[13.75rem]',
              }}
            >
              <a
                href={campaign.model.link}
                target="__blank"
                className="flex items-center gap-1 no-underline font-sans"
              >
                <LinkIcon size={18} fill="#006FEE" className="text-partner" />
                <span className="text-partner text-sm leading-5 truncate max-w-[11.25rem] font-sans">
                  {campaign.model.link}
                </span>
              </a>
            </Snippet>
            <div className="flex items-center gap-1 ml-1">
              <ResponsiveTooltip content={t('campaigns.cell.guestsTooltip')} placement="top">
                <div className="flex items-center gap-1">
                  <EyeIc size={16} className="text-neutral-8" />
                  <span className="text-neutral-8 text-xs font-medium leading-4">{clicks}</span>
                </div>
              </ResponsiveTooltip>
              <ResponsiveTooltip content={t('campaigns.cell.conversionTooltip')} placement="top">
                <span className="text-neutral-8 text-xs font-normal leading-4">
                  ({subscriptionPercent}%)
                </span>
              </ResponsiveTooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignCell;
